/**
 * Lightweight, client-side guard against a shopper submitting more than one review
 * for the same product. Reviewed product ids are stored in localStorage, keyed by
 * customer number (or "guest" when not signed in).
 *
 * Note: this is a best-effort UX guard only. localStorage is per-browser/device, so
 * it does not enforce the rule across devices or against direct API calls.
 */

const STORAGE_KEY_PREFIX = 'bvReviewedProducts'

const isBrowser = () => typeof window !== 'undefined' && !!window.localStorage

const getStorageKey = (customerNo) => `${STORAGE_KEY_PREFIX}:${customerNo || 'guest'}`

const readReviewedProductIds = (customerNo) => {
    if (!isBrowser()) return []
    try {
        const raw = window.localStorage.getItem(getStorageKey(customerNo))
        if (!raw) return []
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed : []
    } catch (e) {
        return []
    }
}

/**
 * @param {string} customerNo - Signed-in customer number, or falsy for guests.
 * @param {string} productId - Product identifier the review is stored against.
 * @returns {boolean} True if a review for this product was already submitted from this browser.
 */
const hasSubmittedReview = (customerNo, productId) => {
    if (!productId) return false
    return readReviewedProductIds(customerNo).includes(productId)
}

/**
 * Record that a review was submitted for the given product.
 * @param {string} customerNo - Signed-in customer number, or falsy for guests.
 * @param {string} productId - Product identifier the review is stored against.
 */
const markReviewSubmitted = (customerNo, productId) => {
    if (!isBrowser() || !productId) return
    try {
        const ids = readReviewedProductIds(customerNo)
        if (ids.includes(productId)) return
        ids.push(productId)
        window.localStorage.setItem(getStorageKey(customerNo), JSON.stringify(ids))
    } catch (e) {
        // localStorage may be unavailable (private mode / quota exceeded); ignore.
    }
}

export {hasSubmittedReview, markReviewSubmitted}
